/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { commonMessages } from '../../../common/messages/common-messages';
import { PulseTile } from '../../../common/ui/pulse-tile';
import { formatGeneratedDate } from '../../../common/utils/format-generated-date';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AnimatedFeedSection } from '../feed-section-animation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { InsightsFeedHeaderProps } from '../types';

import { AppIconStack } from './app-icon-stack/AppIconStack';

const MAX_NAMED_SOURCE_APPS = 4;

const messages = defineMessages({
	insightsFeedTitle: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.insightsFeedTitle',
		defaultMessage: 'Your weekly insights',
		description: 'Title for the InsightsFeed panel header',
	},
	connectMoreApps: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.connectMoreApps',
		defaultMessage: 'Connect more apps to get greater insights.',
		description:
			'Additional summary sentence shown when the user has available apps they can connect from the insights feed',
	},
	keyItemsLabel: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.keyItemsLabel',
		defaultMessage: '{count, plural, one {key item} other {key items}}',
		description:
			'Pluralized noun for the number of key insights Rovo spotted, e.g. "key item" or "key items". Embedded into the insights summary line.',
	},
	appsLabel: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.appsLabel',
		defaultMessage: '{count, plural, one {app} other {apps}}',
		description:
			'Pluralized noun for the number of source apps, e.g. "app" or "apps". Embedded into the insights summary line.',
	},
	summaryWithAppNames: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.summaryWithAppNames',
		defaultMessage:
			'Rovo spotted <b>{insightCount}</b> {keyItems} from across your {appNames} activity this week.',
		description:
			'Insights summary line that names the source apps inline (used when there are a small number of apps). {insightCount} is the number of insights, {keyItems} is the pluralized "key item(s)" noun, and {appNames} is a formatted list of app names such as "Slack, Confluence and Jira".',
	},
	summaryWithAppCount: {
		id: 'rovo-growth-pulse.insights-feed.insights-feed-header.summaryWithAppCount',
		defaultMessage:
			'Rovo spotted <b>{insightCount}</b> {keyItems} from across <b>{appCount}</b> {apps} this week.',
		description:
			'Insights summary line that shows a count of source apps (used when there are too many apps to name individually). {insightCount} is the number of insights, {keyItems} is the pluralized "key item(s)" noun, {appCount} is the number of apps, and {apps} is the pluralized "app(s)" noun.',
	},
});

const GRID_LINE_COLOR = token('color.background.accent.gray.subtler');
const GRID_SUNKEN_COLOR = token('elevation.surface.sunken');
const GRID_PATTERN_BG_IMAGE = `linear-gradient(${GRID_LINE_COLOR} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE_COLOR} 1px, transparent 1px)`;
const GRID_PATTERN_BG_SIZE = '24px 24px';
const GRID_PATTERN_BG_SIZE_FULLSCREEN = '26.4px 26.4px';
const GRID_PATTERN_BG_POSITION = '6px 4px';

const GRID_OVERLAY_BG_IMAGE_SIDEBAR = `linear-gradient(225deg, rgba(248, 248, 248, 0) 0%, ${GRID_SUNKEN_COLOR} 100%)`;
const GRID_OVERLAY_BG_IMAGE_FULLSCREEN_BASE = `linear-gradient(45deg, ${GRID_SUNKEN_COLOR} 0%, rgba(255, 255, 255, 0) 48%, ${GRID_SUNKEN_COLOR} 97%)`;
const GRID_OVERLAY_BG_IMAGE_FULLSCREEN_TOP = `linear-gradient(1deg, ${GRID_SUNKEN_COLOR} 0%, rgba(255, 255, 255, 0) 60%, ${GRID_SUNKEN_COLOR} 96%)`;

const gridLayerContainerStyles = css({
	position: 'absolute',
	inset: 0,
	pointerEvents: 'none',
});

const gridPatternLayerStyles = css({
	position: 'absolute',
	inset: 0,
	opacity: 0.4,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: GRID_PATTERN_BG_IMAGE,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundSize: GRID_PATTERN_BG_SIZE,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundPosition: GRID_PATTERN_BG_POSITION,
});

const gridPatternLayerFullScreenStyles = css({
	position: 'absolute',
	inset: 0,
	opacity: 0.4,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: GRID_PATTERN_BG_IMAGE,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundSize: GRID_PATTERN_BG_SIZE_FULLSCREEN,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundPosition: GRID_PATTERN_BG_POSITION,
});

const gridOverlayLayerStyles = css({
	position: 'absolute',
	inset: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: GRID_OVERLAY_BG_IMAGE_SIDEBAR,
});

const gridOverlayLayerFullScreenBaseStyles = css({
	position: 'absolute',
	inset: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: GRID_OVERLAY_BG_IMAGE_FULLSCREEN_BASE,
});

const gridOverlayLayerFullScreenTopStyles = css({
	position: 'absolute',
	inset: 0,
	opacity: 0.7,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: GRID_OVERLAY_BG_IMAGE_FULLSCREEN_TOP,
});

const styles = cssMap({
	headerWrapper: {
		position: 'relative',
		overflow: 'hidden',
		marginBottom: token('space.200'),
	},
	headerWrapperWithGrid: {
		position: 'relative',
		overflow: 'hidden',
		marginBottom: token('space.200'),
		paddingBlockStart: token('space.800'),
		paddingBlockEnd: token('space.500'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
	},
	headerContent: {
		position: 'relative',
		paddingBottom: token('space.050'),
	},
	headerContentCentered: {
		position: 'relative',
		paddingBottom: token('space.050'),
		width: '100%',
		'@media (min-width: 64rem)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			maxWidth: 'min(800px, 50vw)',
			marginInline: 'auto',
		},
	},
	summaryWrapper: {
		position: 'relative',
		overflow: 'hidden',
	},
	summaryWrapperWithGrid: {
		position: 'relative',
		overflow: 'hidden',
		paddingBlock: token('space.250'),
		paddingInline: token('space.250'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	gridContent: {
		position: 'relative',
	},
	headerRow: {
		alignItems: 'center',
	},
	titleColumn: {
		flex: '1 1 auto',
		minWidth: '0',
	},
	summaryRow: {
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	summaryColumnFullScreen: {
		flex: '1 1 auto',
		minWidth: '0',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		maxWidth: '720px',
	},
	summaryContentFullScreen: {
		display: 'inline',
	},
	connectAppsActionFullScreen: {
		flex: '0 0 auto',
		marginBlockEnd: token('space.100'),
	},
	connectAppsActionSidebar: {
		display: 'block',
	},
	connectAppsActionHidden: {
		display: 'none',
	},
});

const GridBackgroundLayers = ({ variant }: { variant: 'sidebar' | 'fullScreen' }): JSX.Element =>
	variant === 'fullScreen' ? (
		<div aria-hidden="true" css={gridLayerContainerStyles}>
			<div css={gridPatternLayerFullScreenStyles} />
			<div css={gridOverlayLayerFullScreenBaseStyles} />
			<div css={gridOverlayLayerFullScreenTopStyles} />
		</div>
	) : (
		<div aria-hidden="true" css={gridLayerContainerStyles}>
			<div css={gridPatternLayerStyles} />
			<div css={gridOverlayLayerStyles} />
		</div>
	);

const renderSummaryWithBoldNumbers = (summary: string): ReactNode[] => {
	// Temporary until the backend provides structured summary counts.
	return summary.split(/(\d+)/g).map((part, index) => {
		if (/^\d+$/.test(part)) {
			return (
				<Text as="span" weight="bold" key={`${part}-${index}`}>
					{part}
				</Text>
			);
		}

		return part;
	});
};

/** Renders a count wrapped in bold, used as the `<b>` chunk for summary messages. */
const boldChunk = (chunks: ReactNode[]): ReactNode => (
	<Text as="span" weight="bold">
		{chunks}
	</Text>
);

export const InsightsFeedHeader = ({
	title,
	summary,
	summaryDetails,
	generatedAt,
	isFullScreen = false,
	connectAppsAction,
	animationEnabled = false,
	animationStartIndex = 0,
}: InsightsFeedHeaderProps): JSX.Element => {
	const { formatMessage, formatList, locale } = useIntl();
	// The action reports via onVisibilityChange whether it rendered anything, so we can hide the
	// wrapper and "Connect more apps" copy when there is nothing to connect.
	const [hasVisibleConnectAppsAction, setHasVisibleConnectAppsAction] = useState(false);
	const connectAppsActionContent = connectAppsAction?.({
		onVisibilityChange: setHasVisibleConnectAppsAction,
	});
	const formattedDate = formatGeneratedDate(
		generatedAt,
		formatMessage,
		commonMessages.generatedAt,
		locale,
	);
	const connectMoreAppsNode = hasVisibleConnectAppsAction ? (
		<Text size="medium" color="color.text.subtle">
			<FormattedMessage {...messages.connectMoreApps} />
		</Text>
	) : null;
	const connectMoreAppsInlineNode = hasVisibleConnectAppsAction ? (
		<span>
			{' '}
			<FormattedMessage {...messages.connectMoreApps} />
		</span>
	) : null;

	const hasSummaryDetails = summaryDetails !== undefined;

	const namedSourceApps =
		summaryDetails !== undefined ? summaryDetails.sourceApps.slice(0, summaryDetails.appCount) : [];

	const shouldListAppNames =
		summaryDetails !== undefined &&
		summaryDetails.appCount > 0 &&
		summaryDetails.appCount <= MAX_NAMED_SOURCE_APPS &&
		namedSourceApps.length === summaryDetails.appCount;

	const structuredSummaryNode =
		summaryDetails !== undefined
			? shouldListAppNames
				? formatMessage(messages.summaryWithAppNames, {
						insightCount: summaryDetails.insightCount,
						keyItems: formatMessage(messages.keyItemsLabel, {
							count: summaryDetails.insightCount,
						}),
						appNames: formatList(
							namedSourceApps.map((app) => app.name),
							{
								type: 'conjunction',
							},
						),
						b: boldChunk,
					})
				: formatMessage(messages.summaryWithAppCount, {
						insightCount: summaryDetails.insightCount,
						keyItems: formatMessage(messages.keyItemsLabel, {
							count: summaryDetails.insightCount,
						}),
						appCount: summaryDetails.appCount,
						apps: formatMessage(messages.appsLabel, { count: summaryDetails.appCount }),
						b: boldChunk,
					})
			: null;

	const iconStackNode =
		summaryDetails !== undefined && summaryDetails.sourceApps.length > 0 ? (
			<AppIconStack
				sourceApps={summaryDetails.sourceApps}
				appCount={summaryDetails.appCount}
				appearance={isFullScreen ? 'inline' : 'block'}
				testId="insights-feed-app-icon-stack"
			/>
		) : null;

	const summaryTextNode = hasSummaryDetails ? (
		<Text as="span" size="medium" color="color.text.subtle" testId="insights-feed-summary">
			{structuredSummaryNode}
			{isFullScreen ? null : connectMoreAppsInlineNode}
		</Text>
	) : summary ? (
		<Text as="span" size="medium" color="color.text.subtle" testId="insights-feed-summary">
			{renderSummaryWithBoldNumbers(summary)}
			{isFullScreen ? null : connectMoreAppsInlineNode}
		</Text>
	) : null;

	const hasSummaryContent = summaryTextNode !== null;
	const headerRowNode = (
		<Flex gap="space.150" xcss={styles.headerRow}>
			{/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage */}
			<PulseTile backgroundColor="color.background.accent.gray.subtlest" />
			<Box xcss={styles.titleColumn}>
				<Stack space="space.025">
					{formattedDate ? (
						<Text size="small" color="color.text.subtlest" testId="insights-feed-generated-date">
							{formattedDate}
						</Text>
					) : null}
					<Heading size="large">
						{title ?? <FormattedMessage {...messages.insightsFeedTitle} />}
					</Heading>
				</Stack>
			</Box>
		</Flex>
	);
	const animatedHeaderRowNode = isFullScreen ? (
		headerRowNode
	) : (
		<AnimatedFeedSection
			index={animationStartIndex}
			isEnabled={animationEnabled}
			testId="insights-feed-title-animation-section"
		>
			{headerRowNode}
		</AnimatedFeedSection>
	);

	const summaryContentNode = (
		<Box xcss={isFullScreen ? styles.summaryWrapper : styles.summaryWrapperWithGrid}>
			{isFullScreen ? null : <GridBackgroundLayers variant="sidebar" />}
			{isFullScreen ? (
				<Flex gap="space.200" xcss={styles.summaryRow}>
					<Box xcss={styles.summaryColumnFullScreen}>
						<Stack space="space.050">
							<Flex gap="space.075" xcss={styles.summaryContentFullScreen}>
								{summaryTextNode}
								{iconStackNode}
							</Flex>
							{connectMoreAppsNode}
						</Stack>
					</Box>
					{connectAppsActionContent != null ? (
						<Box
							xcss={
								hasVisibleConnectAppsAction
									? styles.connectAppsActionFullScreen
									: styles.connectAppsActionHidden
							}
						>
							{connectAppsActionContent}
						</Box>
					) : null}
				</Flex>
			) : (
				<Stack xcss={styles.gridContent} space="space.200">
					{iconStackNode}
					<Stack space="space.150">
						{summaryTextNode}
						{connectAppsActionContent != null ? (
							<Box
								xcss={
									hasVisibleConnectAppsAction
										? styles.connectAppsActionSidebar
										: styles.connectAppsActionHidden
								}
							>
								{connectAppsActionContent}
							</Box>
						) : null}
					</Stack>
				</Stack>
			)}
		</Box>
	);
	const animatedSummaryContentNode = isFullScreen ? (
		summaryContentNode
	) : (
		<AnimatedFeedSection
			index={animationStartIndex + 1}
			isEnabled={animationEnabled}
			testId="insights-feed-summary-animation-section"
		>
			{summaryContentNode}
		</AnimatedFeedSection>
	);

	return (
		<Box xcss={isFullScreen ? styles.headerWrapperWithGrid : styles.headerWrapper}>
			{isFullScreen ? <GridBackgroundLayers variant="fullScreen" /> : null}
			<Stack
				xcss={isFullScreen ? styles.headerContentCentered : styles.headerContent}
				space="space.200"
			>
				{animatedHeaderRowNode}
				{hasSummaryContent ? animatedSummaryContentNode : null}
			</Stack>
		</Box>
	);
};
